import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Avatar,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Badge,
  Stack,
  AlertTitle
} from '@mui/material';
import {
  People,
  Work,
  Assignment,
  TrendingUp,
  Visibility,
  PersonAdd,
  AssignmentTurnedIn,
  Schedule,
  CheckCircle,
  Warning,
  Business,
  LocationOn,
  ArrowForward,
  VideoCall,
  CalendarToday,
  Timer,
  Assessment,
  Notifications,
  Dashboard,
  Settings,
  Analytics,
  Refresh,
  Download,
  FilterList,
  MoreVert,
  Star,
  StarBorder,
  TrendingDown,
  TrendingFlat,
  AccessTime,
  CheckCircleOutline,
  Cancel,
  Pending,
  PlayArrow,
  Stop,
  Edit,
  Delete,
  Email,
  Phone,
  LinkedIn,
  GitHub,
  Language,
  School,
  Psychology,
  Engineering,
  Code,
  BarChart,
  PieChart,
  ShowChart,
  Timeline,
  Assessment as AssessmentIcon,
  Speed,
  Visibility as VisibilityIcon,
  Group,
  BusinessCenter,
  AttachMoney,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AdminPanelSettings,
  Security,
  ManageAccounts,
  BusinessCenter as BusinessCenterIcon,
  Assignment as AssignmentIcon,
  VideoCall as VideoCallIcon,
  Assessment as AssessmentIcon2,
  People as PeopleIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Dashboard Data States
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    pendingApplications: 0,
    totalSubmissions: 0,
    scheduledInterviews: 0,
    completedInterviews: 0,
    hiredThisMonth: 0,
    newApplicationsToday: 0
  });

  const [usersByRole, setUsersByRole] = useState({});
  const [applicationsByStatus, setApplicationsByStatus] = useState({});
  const [recentApplications, setRecentApplications] = useState([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, [refreshKey]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all data in parallel for better performance
      const [
        adminStatsRes,
        applicationsRes,
        interviewsRes
      ] = await Promise.all([
        axios.get('http://localhost:5000/api/admin/stats', { headers }),
        axios.get('http://localhost:5000/api/applications/admin/all?limit=10', { headers }),
        axios.get('http://localhost:5000/api/interviews?upcoming=true&limit=5', { headers })
      ]);

      // Process admin stats
      const adminStats = adminStatsRes.data.data;
      const applications = applicationsRes.data.data || [];
      const interviews = interviewsRes.data.data || [];

      // Calculate additional metrics
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      const hiredThisMonth = applications.filter(app => 
        app.status === 'hired' && 
        new Date(app.updatedAt) >= startOfMonth
      ).length;

      const newApplicationsToday = applications.filter(app =>
        new Date(app.createdAt) >= startOfToday
      ).length;

      setStats({
        totalUsers: adminStats.totalUsers || 0,
        totalJobs: adminStats.totalJobs || 0,
        activeJobs: adminStats.activeJobs || 0,
        totalApplications: adminStats.totalApplications || 0,
        pendingApplications: adminStats.pendingApplications || 0,
        totalSubmissions: adminStats.totalSubmissions || 0,
        scheduledInterviews: interviews.length,
        completedInterviews: 0,
        hiredThisMonth,
        newApplicationsToday
      });

      setUsersByRole(adminStats.usersByRole || {});
      setApplicationsByStatus(adminStats.applicationsByStatus || {});
      setRecentApplications(applications);
      setUpcomingInterviews(interviews);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': 'info',
      'under_review': 'warning',
      'shortlisted': 'success',
      'interview_scheduled': 'primary',
      'rejected': 'error',
      'hired': 'success',
      'withdrawn': 'default',
      'active': 'success',
      'paused': 'warning',
      'closed': 'error',
      'completed': 'success',
      'pending': 'warning',
      'in_progress': 'info'
    };
    return colors[status] || 'default';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDaysAgo = (date) => {
    const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  const adminActions = [
    {
      title: 'Analytics Dashboard',
      description: 'View comprehensive hiring analytics and performance metrics',
      icon: <AnalyticsIcon />,
      color: '#2196f3',
      path: '/analytics-dashboard',
      badge: null
    },
    {
      title: 'Manage Jobs',
      description: 'Create, edit, and manage job postings',
      icon: <Work />,
      color: '#4caf50',
      path: '/manage-jobs',
      badge: stats.activeJobs
    },
    {
      title: 'Review Applications',
      description: 'Review and manage candidate applications',
      icon: <Assignment />,
      color: '#ff9800',
      path: '/admin/applications',
      badge: stats.pendingApplications
    },
    {
      title: 'Schedule Interviews',
      description: 'Schedule and manage candidate interviews',
      icon: <VideoCall />,
      color: '#9c27b0',
      path: '/task-submissions',
      badge: stats.scheduledInterviews
    },
    {
      title: 'Manage Users',
      description: 'Manage system users and permissions',
      icon: <People />,
      color: '#607d8b',
      path: '/admin/users',
      badge: null
    },
    {
      title: 'Create Tasks',
      description: 'Create assessment tasks for candidates',
      icon: <Assessment />,
      color: '#e91e63',
      path: '/create-task',
      badge: null
    }
  ];

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Administrator Portal
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
              Welcome back, {user?.profile?.firstName || 'Administrator'}! Manage your hiring platform
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Analytics />}
              onClick={() => navigate('/analytics-dashboard')}
            >
              View Analytics
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}
      </Box>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #4caf5015, #4caf5025)',
            border: '1px solid #4caf5030'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                    {stats.totalUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    +{stats.newApplicationsToday} new today
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#4caf5020',
                  color: '#4caf50'
                }}>
                  <People sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #2196f315, #2196f325)',
            border: '1px solid #2196f330'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                    {stats.totalApplications}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Applications
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.pendingApplications} pending review
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#2196f320',
                  color: '#2196f3'
                }}>
                  <Assignment sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #9c27b015, #9c27b025)',
            border: '1px solid #9c27b030'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                    {stats.scheduledInterviews}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled Interviews
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.completedInterviews} completed
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#9c27b020',
                  color: '#9c27b0'
                }}>
                  <VideoCall sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #ff980015, #ff980025)',
            border: '1px solid #ff980030'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                    {stats.hiredThisMonth}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hired This Month
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    +{stats.newApplicationsToday} applications today
                  </Typography>
                </Box>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#ff980020',
                  color: '#ff9800'
                }}>
                  <TrendingUp sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Admin Actions */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {adminActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                height: '100%', 
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                }
              }}
              onClick={() => navigate(action.path)}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: '50%', 
                  bgcolor: `${action.color}20`,
                  color: action.color,
                  width: 60,
                  height: 60,
                  margin: '0 auto 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {action.title}
                  {action.badge && (
                    <Badge 
                      badgeContent={action.badge} 
                      color="error" 
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={4}>
        {/* Recent Applications */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                Recent Applications
              </Typography>
              <Button
                variant="outlined"
                size="small"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/admin/applications')}
              >
                View All
              </Button>
            </Box>
            
            {recentApplications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No recent applications to display.
                </Typography>
              </Box>
            ) : (
              <List>
                {recentApplications.slice(0, 5).map((app, index) => (
                  <React.Fragment key={app._id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: getStatusColor(app.status) + '.light' }}>
                          {app.candidate?.profile?.firstName?.[0] || 'U'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {app.candidate?.profile?.firstName} {app.candidate?.profile?.lastName}
                            </Typography>
                            <Chip 
                              label={app.status} 
                              size="small"
                              color={getStatusColor(app.status)}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {app.job?.title} • {app.job?.department}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Applied {calculateDaysAgo(app.createdAt)}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          onClick={() => navigate('/admin/applications')}
                        >
                          <Visibility />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < Math.min(recentApplications.length, 5) - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Upcoming Interviews */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                Upcoming Interviews
              </Typography>
              <Chip 
                label={upcomingInterviews.length} 
                size="small" 
                color="primary"
              />
            </Box>
            
            {upcomingInterviews.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No upcoming interviews scheduled.
                </Typography>
              </Box>
            ) : (
              <List>
                {upcomingInterviews.map((interview, index) => (
                  <React.Fragment key={interview._id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          <VideoCall />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {interview.candidate?.profile?.firstName} {interview.candidate?.profile?.lastName}
                            </Typography>
                            <Chip 
                              label={interview.interviewType} 
                              size="small"
                              color="primary"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(interview.scheduledDate)} at {formatTime(interview.scheduledDate)}
                            </Typography>
                            {interview.job && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {interview.job.title}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Join Interview">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => window.open(interview.meetingLink, '_blank')}
                          >
                            <VideoCall />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < upcomingInterviews.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminDashboard;
